import { useEffect, useRef, useState } from 'react'
import { ArrowDown, ArrowRight, Bot, Clock3, Eye, HeartPulse, Microscope, Radio, ScanLine, Wallet } from 'lucide-react'
import { Link } from 'react-router-dom'
import scenes from '../home-photos.json'
import spineIcon from '../assets/healthicons/spine.svg'
import pancreasIcon from '../assets/healthicons/pancreas.svg'
import bloodCellsIcon from '../assets/healthicons/blood-cells.svg'

function TheatrePhoto({ scene, active, priority }: { scene: typeof scenes[number]; active: boolean; priority: boolean }) {
  const edges = useRef<(HTMLCanvasElement | null)[]>([])
  const imageRef = useRef<HTMLImageElement>(null)
  const drawEdges = async (image: HTMLImageElement) => {
    // Sample only the outermost pixel columns; the photograph itself stays untouched.
    // srcset density can change naturalWidth; the bitmap gives actual source pixel bounds.
    const bitmap = await createImageBitmap(image)
    const { width, height } = bitmap
    edges.current.forEach((canvas, index) => {
      if (!canvas) return
      canvas.width = 64
      canvas.height = Math.min(height, 1024)
      const context = canvas.getContext('2d')
      if (!context) return
      context.drawImage(bitmap, index === 1 ? width - 1 : 0, 0,
        1, height, 0, 0, canvas.width, canvas.height)
      const fade = context.createLinearGradient(index === 1 ? canvas.width : 0, 0,
        index === 0 ? canvas.width : 0, 0)
      fade.addColorStop(0, 'transparent')
      fade.addColorStop(.04, 'transparent')
      fade.addColorStop(.92, '#000')
      fade.addColorStop(1, '#000')
      context.globalCompositeOperation = 'destination-in'
      context.fillStyle = fade
      context.fillRect(0, 0, canvas.width, canvas.height)
    })
    bitmap.close()
  }

  useEffect(() => {
    const image = imageRef.current
    if (image?.complete && image.naturalWidth) void drawEdges(image)
  }, [])

  return <div className={active ? 'theatre-scene is-active' : 'theatre-scene'} aria-hidden={!active}
    style={{ width: `calc(100cqh * ${scene.width / scene.height})` }}>
    {['left', 'right'].map((direction, index) => <canvas key={direction}
      ref={(canvas) => { edges.current[index] = canvas }} className={`theatre-edge theatre-edge-${direction}`} aria-hidden="true" />)}
    <img ref={imageRef} src={scene.src} srcSet={scene.srcSet} sizes={`${100 * scene.width / scene.height}vh`}
      alt={scene.alt} width={scene.width} height={scene.height} fetchPriority={priority ? 'high' : 'auto'}
      loading={priority ? 'eager' : 'lazy'} decoding="async" onLoad={(event) => drawEdges(event.currentTarget)} />
  </div>
}

function TheatreCarousel() {
  const [active, setActive] = useState(0)
  const [loaded, setLoaded] = useState(() => new Set([0, 1]))
  const [reducedMotion, setReducedMotion] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    setLoaded((previous) => new Set([...previous, active, (active + 1) % scenes.length]))
  }, [active])

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReducedMotion(preference.matches)
    update()
    preference.addEventListener('change', update)
    return () => preference.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    if (reducedMotion || hovered || focused) return
    const timer = window.setInterval(() => {
      if (!document.hidden) setActive((index) => (index + 1) % scenes.length)
    }, 8000)
    return () => window.clearInterval(timer)
  }, [reducedMotion, hovered, focused])

  return <div className="theatre-carousel" role="region" aria-roledescription="carousel" aria-label="Medical care photographs"
    onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
    onFocusCapture={() => setFocused(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false) }}>
    {scenes.map((scene, index) => loaded.has(index) && <TheatrePhoto key={scene.src} scene={scene}
      active={index === active} priority={index === 0} />)}
    <div className="theatre-caption">
      <div className="theatre-controls">
        {scenes.map((_, index) => <button key={index} className={index === active ? 'scene-dot is-active' : 'scene-dot'}
          aria-label={`Show image ${index + 1} of ${scenes.length}`} aria-pressed={index === active}
          onClick={() => setActive(index)}><span /></button>)}
      </div>
    </div>
  </div>
}

const reasons = [
  { label: 'Less waiting, more value', title: 'Your time. Your budget.', icons: [Clock3, Wallet],
    summary: 'In some countries, seeing a specialist can mean a long wait or a high private-care bill.',
    detail: 'Compare available dates and a written estimate for your case before making travel plans.' },
  { label: 'Technology & treatment', title: 'Modern care. More possibilities.', icons: [Bot, Microscope],
    summary: 'China is a major hub for advanced medical technology.',
    detail: 'Available equipment and medicines depend on the hospital and your case. Clinical trials have specific eligibility requirements.' },
  { label: 'Expertise & outcomes', title: 'Care for your condition.', icons: [HeartPulse],
    summary: 'China has distinctive treatment approaches and extensive experience across many conditions.',
    detail: 'Understand the evidence, benefits and risks for your condition before deciding on treatment.' },
]

function SpecialistAccessDetails() {
  return <div className="access-highlights">
    <div className="access-comparison">
      <div className="access-highlight">
        <Clock3 size={28} strokeWidth={1.4} aria-hidden="true" />
        <div className="access-figures" aria-label="Illustrative specialist waiting time: three or more months to an estimated one to two weeks">
          <strong className="access-before">3+ months</strong>
          <ArrowDown size={26} strokeWidth={1.4} aria-hidden="true" />
          <strong className="access-after">1–2 weeks</strong>
        </div>
        <p className="access-metric-label">To see a specialist</p>
      </div>
      <div className="access-highlight">
        <Wallet size={28} strokeWidth={1.4} aria-hidden="true" />
        <div className="access-figures access-cost"><strong>From ~20%</strong></div>
        <p className="access-metric-label">of a US base-package price</p>
      </div>
    </div>
  </div>
}

function ExpertiseStories() {
  const stories = [
    { icon: spineIcon, title: 'Severe scoliosis', country: 'UK',
      before: '104° curve despite bracing', after: '~80% correction; walking within a week',
      source: 'Shanghai government', url: 'https://english.shanghai.gov.cn/en-ExpatStories-WhatisNew/20250805/cc5c4e9c89f343e2802d42f0e0394e0b.html',
      frenchUrl: 'https://www.20minutes.fr/monde/royaume-uni/4169511-20250825-chine-jeune-britannique-operee-succes-chine-rare-double-scoliose' },
    { icon: pancreasIcon, title: 'Pancreatic tumour', country: 'Canada',
      before: 'Spleen removal advised', after: 'Tumour removed; spleen preserved',
      source: 'Yicai Global', url: 'https://www.yicaiglobal.com/news/chinas-medical-technology-on-par-with-developed-countries-attract-foreign-patients' },
    { icon: bloodCellsIcon, title: 'Lymphoma', country: 'New Zealand',
      before: '3 chemo rounds failed', after: 'CAR-T; complete remission at 3-month check',
      source: 'China Daily', url: 'https://global.chinadaily.com.cn/a/202412/06/WS6752e602a310f1265a1d19b6.html' },
  ]
  return <div className="expertise-stories">
    {stories.map(({ icon, title, country, before, after, source, url, frenchUrl }) => <div className="expertise-story" key={title}>
      <div className="expertise-condition"><span className="expertise-condition-icon" style={{ maskImage: `url("${icon}")`, WebkitMaskImage: `url("${icon}")` }} aria-hidden="true" /><h4>{title}</h4></div>
      <div><div className="expertise-comparison">
        <div><span className="expertise-country">{country}</span><p>{before}</p></div>
        <ArrowRight size={20} strokeWidth={1.4} aria-label="followed by treatment in China" />
        <div><span className="expertise-country">China</span><p>{after}</p></div>
      </div><div className="expertise-story-links">
        <a href={url} target="_blank" rel="noreferrer" aria-label={`Read ${title} story at ${source}`}><em>Know more</em></a>
        {frenchUrl && <a href={frenchUrl} hrefLang="fr" target="_blank" rel="noreferrer" aria-label="Read the scoliosis story in French at 20 Minutes"><em>Read in French</em></a>}
      </div></div>
    </div>)}
    <p className="expertise-disclaimer">Each patient's situation requires individual assessment.</p>
  </div>
}

function TechnologyStats({ expanded }: { expanded: boolean }) {
  return <dl className="access-stats technology-stats" id="why-detail-1" hidden={!expanded}>
    <div><dt><ScanLine size={26} strokeWidth={1.4} aria-hidden="true" />Gamma Knife</dt><dd><strong>#1</strong><span>number of cases by a single hospital</span></dd></div>
    <div><dt><Radio size={26} strokeWidth={1.4} aria-hidden="true" />Focused ultrasound</dt><dd><strong>~33%</strong><span>of global treatment sites</span></dd></div>
    <div><dt><Eye size={26} strokeWidth={1.4} aria-hidden="true" />Refractive surgery</dt><dd><strong>≥50%</strong><span>of procedures worldwide</span></dd></div>
    <div><dt><Microscope size={26} strokeWidth={1.4} aria-hidden="true" />CAR-T trials</dt><dd><strong>~53%</strong><span>of global CAR-T trials</span></dd></div>
  </dl>
}

export function Home() {
  const [expanded, setExpanded] = useState<number | null>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const storyRef = useRef<HTMLDivElement>(null)

  const goToSlide = (index: number) => {
    const story = storyRef.current
    if (!story) return
    const introduction = story.firstElementChild as HTMLElement
    story.scrollTo({ top: index === 0 ? 0 : introduction.offsetHeight,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })
  }

  return <div className="home-story" role="region" aria-label="Home slides" tabIndex={0} ref={storyRef}
    onScroll={(event) => {
      const story = event.currentTarget
      const introduction = story.firstElementChild as HTMLElement
      setCurrentSlide(story.scrollTop >= introduction.offsetHeight / 2 ? 1 : 0)
    }}>
    <div className="home-intro">
      <TheatreCarousel />
      <div className="care-hero-copy">
        <div><h1>Treatment in China, <em>made easier.</em></h1>
          <p className="care-lede">We help you connect with specialists, bridge the language gap and coordinate your care journey.</p></div>

      </div>
    </div>

    <section className="why-china section-pad" aria-labelledby="why-china-title"
      onPointerLeave={(event) => { if (event.pointerType === 'mouse') setExpanded(null) }}>
      <div className="why-heading" onPointerEnter={(event) => { if (event.pointerType === 'mouse') setExpanded(null) }}><p className="eyebrow">A destination worth exploring</p><h2 id="why-china-title">Why China?</h2><p>More possibilities for your next step in care.</p></div>
      <div className="why-panels">
        {reasons.map((reason, index) => <article key={reason.label} className={`why-panel why-panel-evidence${index === 1 ? ' why-panel-technology' : ''}${expanded === index ? ' is-expanded' : ''}`}
          onPointerEnter={(event) => { if (event.pointerType === 'mouse') setExpanded(index) }}
          onBlurCapture={(event) => {
            // Keep the next touch target in place until its click switches cards.
            const movingBetweenMobileCards = window.matchMedia('(max-width: 620px)').matches
              && event.currentTarget.parentElement?.contains(event.relatedTarget)
            if (!movingBetweenMobileCards && !event.currentTarget.contains(event.relatedTarget)) setExpanded(null)
          }}>
          <button className="why-panel-toggle" aria-expanded={expanded === index} aria-controls={`why-detail-${index}`}
            onClick={(event) => {
              const mouseClick = event.detail > 0 && 'pointerType' in event.nativeEvent && event.nativeEvent.pointerType === 'mouse'
              setExpanded((current) => mouseClick ? index : current === index ? null : index)
            }}>
            <span className="why-panel-top"><span>0{index + 1}</span><span>{reason.label}</span><ArrowRight size={18} /></span>
            <span className="why-icons" aria-hidden="true">{reason.icons.map((Icon, iconIndex) => <Icon key={iconIndex} size={36} strokeWidth={1.25} />)}</span>
            <h3>{reason.title}</h3>
            <span className="why-summary">{reason.summary}</span>
          </button>
          {index === 1 && <TechnologyStats expanded={expanded === index} />}
          {index !== 1 && <div className="why-detail" id={`why-detail-${index}`} hidden={expanded !== index}>{index === 0 ? <SpecialistAccessDetails /> : <ExpertiseStories />}</div>}
        </article>)}
      </div>
      <nav className="home-slide-legal" aria-label="Legal"><span>© {new Date().getFullYear()} China Care</span><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link><Link to="/disclaimer">Medical disclaimer</Link></nav>
    </section>
    <nav className="home-slide-dots" aria-label="Homepage sections">
      {['Introduction', 'Why China'].map((label, index) => <button key={label}
        className={currentSlide === index ? 'is-active' : ''} aria-label={`Go to ${label}`}
        aria-current={currentSlide === index ? 'true' : undefined} onClick={() => goToSlide(index)}><span /></button>)}
    </nav>
  </div>
}
